import { Button, TextField } from '@warp-ds/react';
import cn from 'classnames';
import { useEffect, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { Autocomplete } from 'shared/search/common/autocomplete/Autocomplete';
import { fhhAutocompleteMapper } from 'shared/search/common/autocomplete/utils/AutocompleteMapper';
import { generateGooglePlacesSessionToken } from 'shared/search/common/utils/googlePlacesSessionToken';
import { BedroomFilter } from 'shared/search/fhh/BedroomFilter';
import type { AutocompleteOption } from 'shared/search/types';
import { formatISODate } from 'shared/util/datetime-fns';
import type { Filters } from '@/domain';
import styles from './components/NewSearch.module.css';
import { NewSearchDatePickerWrapper } from './components/NewSearchDatePickerWrapper';
import { SearchSubmit } from './components/SearchSubmit';

type FhhSearchProps = {
    filters: Filters;
    openSearchForm: () => void;
};

export const FhhSearch = ({ filters, openSearchForm }: FhhSearchProps) => {
    const { apiExternalUrl } = useConfig();
    const [googlePlacesSessionToken] = useState(generateGooglePlacesSessionToken); // this value must only be generated once per page load

    const emptyDestination: AutocompleteOption = {
        id: '',
        text: '',
    };

    const populateDestination = () =>
        filters.city
            ? {
                  id: `${filters.city}, ${filters.country}`,
                  text: `${filters.city}, ${filters.country}`,
                  city: filters.city,
                  country: filters.country ?? undefined,
                  bounds: {
                      southwest_lat: Number(filters.lat_sw),
                      southwest_lng: Number(filters.lng_sw),
                      northeast_lat: Number(filters.lat_ne),
                      northeast_lng: Number(filters.lng_ne),
                  },
              }
            : filters.country
              ? {
                    id: `${filters.country}`,
                    text: `${filters.country}`,
                    city: undefined,
                    country: filters.country,
                    bounds: {
                        southwest_lat: Number(filters.lat_sw),
                        southwest_lng: Number(filters.lng_sw),
                        northeast_lat: Number(filters.lat_ne),
                        northeast_lng: Number(filters.lng_ne),
                    },
                }
              : emptyDestination;

    const dateOrNull = (date: string): Date | null => (date ? new Date(date) : null);
    const [fromDate, setFromDate] = useState(dateOrNull(filters.booking_from ?? ''));
    const [toDate, setToDate] = useState(dateOrNull(filters.booking_to ?? ''));
    const [destination, setDestination] = useState<AutocompleteOption | null>(populateDestination);
    const [bedrooms, setBedrooms] = useState(filters.no_of_bedrooms_from ? Number(filters.no_of_bedrooms_from) : 0);
    const [beds, setBeds] = useState(filters.no_of_beds_from ? Number(filters.no_of_beds_from) : 0);

    useEffect(() => {
        setFromDate(dateOrNull(filters.booking_from ?? ''));
        setToDate(dateOrNull(filters.booking_to ?? ''));
        setDestination(populateDestination);
        setBedrooms(filters.no_of_bedrooms_from ? Number(filters.no_of_bedrooms_from) : 0);
        setBeds(filters.no_of_beds_from ? Number(filters.no_of_beds_from) : 0);
    }, [filters]);

    const handleDatesChange = ({ startDate, endDate }: { startDate: Date | null; endDate: Date | null }) => {
        if (startDate) {
            setFromDate(dateOrNull(formatISODate(startDate)));
            setToDate(null);
        }
        endDate ? setToDate(dateOrNull(formatISODate(endDate))) : null;
    };

    const onAccommodationChanged = (valueChanged: { no_of_bedrooms_from?: number; no_of_beds_from?: number }): void => {
        if (valueChanged.no_of_bedrooms_from !== undefined) {
            setBedrooms(valueChanged.no_of_bedrooms_from);
        }
        if (valueChanged.no_of_beds_from !== undefined) {
            setBeds(valueChanged.no_of_beds_from);
        }
    };

    const bedroomText = `${beds < 1 ? 'Alle gjester' : beds > 1 ? `${beds} gjester` : '1 gjest'}, ${bedrooms ? `fra ${bedrooms} soverom` : 'Alle soverom'}`;

    return (
        <section
            className="rounded-8 md:p-32 md:border s-border rounded-8 mb-40 s-bg"
            data-testid="fhhNewSearch"
            aria-label="Feriehjem og hytter til leie, nytt søk">
            <div className="flex flex-col md:flex-row gap-12 items-end">
                <div className="w-full md:flex-1">
                    <Autocomplete
                        vertical="fhh"
                        id="newSearchDestination"
                        label="Destinasjon"
                        placeholder="Hvor som helst"
                        defaultValue={
                            destination?.city
                                ? `${destination.city}, ${destination.country}`
                                : destination?.country
                                  ? `${destination.country}`
                                  : ''
                        }
                        url={`${apiExternalUrl}/autocomplete?query=`}
                        headers={{ 'x-gtoken': googlePlacesSessionToken }}
                        mapOptions={fhhAutocompleteMapper}
                        onSelect={(option) => setDestination(option)}
                    />
                </div>
                <div className="w-full md:flex-2">
                    <NewSearchDatePickerWrapper startDate={fromDate} endDate={toDate} useRange onDateChange={handleDatesChange}>
                        <TextField id="newSearchCheckoin" label="Innsjekk" />
                        <TextField id="newSearchCheckout" label="Utsjekk" />
                    </NewSearchDatePickerWrapper>
                </div>
                <div className="w-full md:flex-1">
                    <BedroomFilter
                        data-testid="fhhBedroomFilter"
                        trigger={{
                            text: bedroomText,
                            label: 'Reisende',
                        }}
                        filters={{ no_of_beds_from: beds, no_of_bedrooms_from: bedrooms }}
                        onFilterChanged={onAccommodationChanged}
                    />
                </div>
                <div className="w-full mt-16 md:flex-1">
                    <SearchSubmit
                        destinationData={destination}
                        startDate={fromDate}
                        endDate={toDate}
                        numOfBeds={beds}
                        numOfBedrooms={bedrooms}
                        filters={filters}
                        resultUrl="/reise/feriehus-hytteutleie/resultat/"
                    />
                </div>
            </div>
            <Button secondary className={cn(styles['search-button'], 'my-16 md:hidden')} onClick={openSearchForm}>
                Lukk
            </Button>
        </section>
    );
};
